import { toast } from "@/components/ui/toast";
import {
  DeleteFeedArticle,
  GetFeedArticlesPagedFromProjection,
  type GetFeedArticlesPagedFromProjectionArticle,
  MoveOrRemoveArticlesFromNarrativeCluster,
  UpdateArticle,
} from "@/data-access/news-feeds/queries/articles-management-queries";
import { client } from "@/lib/urqlProvider";
import type { SelectedFiltersInput } from "@/lib/utils/filters";
import { pluralize } from "@/lib/utils/prettyName";
import { refetchNewsFeedFilters } from "../filters/newsFeed-filters.async-actions";
import {
  buildBulkOperationInput,
  buildOverridesObject,
  buildSelectionSnapshot,
} from "./feed-content-management.helpers";
import useFeedContentManagementStore from "./feed-content-management.slice";

const ARTICLES_PER_PAGE = 25;

let currentArticlesController: AbortController | null = null;
/** AbortController for in-flight "load more" requests; aborted when filters change so stale append never applies. */
let currentAppendController: AbortController | null = null;

/** Resets module-level abort controllers. Used in tests so one test cannot affect another. */
export const resetControllers = () => {
  if (currentArticlesController) {
    currentArticlesController.abort();
    currentArticlesController = null;
  }
  if (currentAppendController) {
    currentAppendController.abort();
    currentAppendController = null;
  }
};

/**
 * Fetch articles with optional ordering and pagination
 * Uses filters including feedId, dateRange, and selected filters
 * Uses FeedArticleProjection query which provides effective values (overrides/translations already applied)
 */
export const fetchArticlesPaged = async (
  feedId: string,
  startDate: string,
  endDate: string,
  selectedFilters?: SelectedFiltersInput,
  pagination?: {
    after?: string;
  },
  append = false,
) => {
  if (!append) {
    // Initial fetch: cancel any in-flight initial and any in-flight append so stale data is never applied
    if (currentArticlesController) currentArticlesController.abort();
    if (currentAppendController) {
      currentAppendController.abort();
      currentAppendController = null;
    }
    currentArticlesController = new AbortController();
  } else {
    // Append (load more): cancel any previous append only; use a dedicated controller so we can abort it on filter change
    if (currentAppendController) currentAppendController.abort();
    currentAppendController = new AbortController();
  }

  const controller = append
    ? currentAppendController
    : currentArticlesController;

  const {
    setInitialLoading,
    setFetchingArticles,
    setError,
    setCurrentPage,
    appendArticles,
    searchTerm,
    ordering,
    chartSelection,
  } = useFeedContentManagementStore.getState();

  // Set initial loading state only for first fetch (not for pagination)
  if (!append) {
    setInitialLoading(true);
  }
  setFetchingArticles(true);
  setError(null);

  try {
    if (!feedId || !startDate || !endDate) {
      setError("Missing required parameters: feedId, startDate, or endDate");
      setInitialLoading(false);
      setFetchingArticles(false);
      return;
    }

    const queryOptions: {
      requestPolicy: "network-only";
      fetchOptions?: { signal: AbortSignal };
    } = {
      requestPolicy: "network-only", // Always fetch from network for articles
    };

    if (controller) {
      queryOptions.fetchOptions = { signal: controller.signal };
    }

    const result = await client
      .query(
        GetFeedArticlesPagedFromProjection,
        {
          ordering,
          filters: {
            feedId,
            dateRange: { startDate, endDate },
            state: "ADDED",
            searchMatch: searchTerm,
            ...(chartSelection && { chartSelection }),
            ...selectedFilters,
          },
          first: ARTICLES_PER_PAGE,
          after: pagination?.after || null,
        },
        queryOptions,
      )
      .toPromise();

    // Check if this request was aborted
    if (controller?.signal.aborted) {
      return;
    }

    if (result.error) {
      setError(result.error.message);
      setInitialLoading(false);
      setFetchingArticles(false);
      return;
    }

    if (result.data?.feedArticleProjections) {
      const { edges, pageInfo, totalCount } =
        result.data.feedArticleProjections;

      const paginationInfo = {
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
        startCursor: pageInfo.startCursor,
        endCursor: pageInfo.endCursor,
        totalCount,
      };

      // Use projection articles directly - no transformation needed
      // Cards will consume projection shape directly
      const projectionEdges =
        edges?.map((edge) => ({
          cursor: edge.cursor,
          node: edge.node,
        })) ?? [];

      if (append) {
        appendArticles(projectionEdges, paginationInfo);
        currentAppendController = null;
      } else {
        setCurrentPage(projectionEdges, paginationInfo);
      }
    } else {
      // Handle case where no data is returned
      setInitialLoading(false);
      setFetchingArticles(false);
    }
  } catch (error: unknown) {
    // Ignore aborted request errors
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }

    setError(error instanceof Error ? error.message : "Unknown error");
    setInitialLoading(false);
    setFetchingArticles(false);
  }
};

/**
 * Helper: Handle OperationInfo errors from response array
 * Returns error info if found, null otherwise
 */
export const handleResponseErrors = <TResponse extends { __typename?: string }>(
  responses: TResponse[],
  errorMessage: string,
): { messages: string[] } | null => {
  const errorResponse = responses.find(
    (response) => response.__typename === "OperationInfo",
  ) as
    | (TResponse & {
        __typename: "OperationInfo";
        messages?: { message: string }[];
      })
    | undefined;

  if (errorResponse && errorResponse.__typename === "OperationInfo") {
    const mappedMessages = errorResponse.messages?.map((m) => m.message) ?? [];
    const messages =
      mappedMessages.length > 0 ? mappedMessages : [errorMessage];
    return { messages };
  }

  return null;
};

/**
 * Helper: Handle FeedArticleProjectionListResponse with conditional update/refetch logic
 * If feedArticleIds exists, updates store with articles from response
 * Otherwise, refetches articles using fetchArticlesPaged
 */
export const handleArticleListResponse = async <
  TResponse extends { __typename?: string },
>(
  responses: TResponse[],
  feedArticleIds: string[] | null,
  feedId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
  updateArticlesInStore: (
    articles: GetFeedArticlesPagedFromProjectionArticle[],
  ) => void,
): Promise<void> => {
  const projectionResponse = responses.find(
    (response) => response.__typename === "FeedArticleProjectionListResponse",
  ) as
    | (TResponse & {
        __typename: "FeedArticleProjectionListResponse";
        feedArticleProjection: GetFeedArticlesPagedFromProjectionArticle[];
      })
    | undefined;

  if (
    projectionResponse &&
    projectionResponse.__typename === "FeedArticleProjectionListResponse"
  ) {
    if (feedArticleIds) {
      // Individual selection: use articles from response to update store
      updateArticlesInStore(projectionResponse.feedArticleProjection);
    } else {
      // Select all with filters: refetch articles to get updated list
      await fetchArticlesPaged(
        feedId,
        dateRange.startDate,
        dateRange.endDate,
        selectedFilters,
      );
    }
  } else if (!feedArticleIds) {
    // No article list response but success - refetch to be safe if using filters
    await fetchArticlesPaged(
      feedId,
      dateRange.startDate,
      dateRange.endDate,
      selectedFilters,
    );
  }
};

/**
 * Delete feed articles and refetch filters
 * @param feedId - The feed ID
 * @param dateRange - Date range for filter refetch
 * @param selectedFilters - Selected filters for filter refetch
 * @param reason - Reason for deletion
 * @returns Success status and optional error messages
 */
export const deleteFeedArticles = async (
  feedId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
  reason: string,
) => {
  const state = useFeedContentManagementStore.getState();
  const { deleteArticlesFromStore } = state;
  const { selectedIds: articleIds, isAllSelected } =
    buildSelectionSnapshot(state);

  if (articleIds.length === 0 && !isAllSelected) {
    toast({
      type: "error",
      title: "No articles selected",
      description: "Please select at least one article to delete.",
    });
    return {
      success: false,
      errors: ["No articles selected"],
    };
  }

  // Build bulk operation input structure
  const { excludedIds, feedArticleIds, projectionFilters } =
    buildBulkOperationInput(state, dateRange, selectedFilters);

  try {
    const result = await client
      .mutation(DeleteFeedArticle, {
        input: {
          feedId,
          feedArticleId: feedArticleIds, // GraphQL schema uses feedArticleId (singular)
          excludedIds,
          projectionFilters,
          reason,
        },
      })
      .toPromise();

    // Handle OperationInfo errors
    if (result.data?.deleteFeedArticle.__typename === "OperationInfo") {
      const messages =
        result.data.deleteFeedArticle.messages?.map((m) => m.message) ?? [];

      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description:
          messages[0] || "We couldn't remove those articles right now.",
      });

      return {
        success: false,
        errors: messages,
      };
    }

    // Handle SuccessResponse
    if (result.data?.deleteFeedArticle.__typename === "SuccessResponse") {
      // Remove articles from local state (articlesMap and currentPage.edges)
      if (feedArticleIds) {
        // Individual selection: remove specific articles from store
        deleteArticlesFromStore(feedArticleIds);
      } else {
        // Select all with filters: refetch articles to get updated list
        // This ensures we have the correct articles after deletion
        await fetchArticlesPaged(
          feedId,
          dateRange.startDate,
          dateRange.endDate,
          selectedFilters,
        );
      }

      // Refetch filters to update counts
      await refetchNewsFeedFilters(feedId, dateRange, selectedFilters);

      const { selectedCount } = buildSelectionSnapshot(state);
      const count = selectedCount;
      toast({
        type: "success",
        title: "Done! Articles removed",
        description: `Removed ${count} ${pluralize(count, "article")} from the feed.`,
      });

      return {
        success: true,
        message: result.data.deleteFeedArticle.message,
      };
    }

    // Handle unexpected errors
    if (result.error) {
      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description: "We couldn't remove those articles right now.",
      });

      return {
        success: false,
        errors: [result.error.message],
      };
    }

    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't remove those articles right now.",
    });

    return {
      success: false,
      errors: ["Unknown error occurred"],
    };
  } catch (error) {
    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't remove those articles right now.",
    });

    return {
      success: false,
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};

/**
 * Update/edit feed articles
 * Gets article IDs from store (singleArticleIdForAction or selectedArticleIds)
 * @param feedId - The feed ID
 * @param dateRange - Date range for filter refetch
 * @param selectedFilters - Selected filters for filter refetch
 * @returns Success status and optional error messages
 */
export const updateFeedArticle = async (
  feedId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
) => {
  const state = useFeedContentManagementStore.getState();
  const {
    editArticle,
    articlesMap,
    resetEditModal,
    singleArticleIdForAction,
    updateArticlesInStore,
  } = state;
  const { selectedIds: articleIdsToUpdate, isAllSelected } =
    buildSelectionSnapshot(state);

  if (articleIdsToUpdate.length === 0 && !isAllSelected) {
    toast({
      type: "error",
      title: "No articles selected",
      description: "Please select at least one article to update.",
    });
    return {
      success: false,
      errors: ["No articles selected"],
    };
  }

  // Set loading state directly
  useFeedContentManagementStore.setState(
    { isUpdatingArticle: true },
    false,
    "[User] Article Edit: Start updating article",
  );

  try {
    // Build overrides object from editArticle state
    // For single edit: compare with original and only send changed fields
    // For bulk edit: send all fields
    const overrides = buildOverridesObject(
      editArticle,
      articlesMap,
      singleArticleIdForAction,
    );

    // Build bulk operation input structure
    const { excludedIds, feedArticleIds, projectionFilters } =
      buildBulkOperationInput(state, dateRange, selectedFilters);

    const result = await client
      .mutation(UpdateArticle, {
        feedId,
        feedArticleIds,
        excludedIds,
        projectionFilters,
        overrides,
      })
      .toPromise();

    if (!result.data?.feedArticleOverride) {
      throw new Error("No response data from mutation");
    }

    // The mutation returns an array of union type responses
    const responses = result.data.feedArticleOverride;

    // Check for errors
    const errorInfo = handleResponseErrors(
      responses,
      "We couldn't update that article right now.",
    );

    if (errorInfo) {
      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description:
          errorInfo.messages[0] || "We couldn't update that article right now.",
      });

      useFeedContentManagementStore.setState(
        { isUpdatingArticle: false },
        false,
        "[User] Article Edit: Update failed",
      );
      return {
        success: false,
        errors: errorInfo.messages,
      };
    }

    // Handle article list response with conditional update/refetch logic
    await handleArticleListResponse(
      responses,
      feedArticleIds,
      feedId,
      dateRange,
      selectedFilters,
      updateArticlesInStore,
    );

    useFeedContentManagementStore.setState(
      { isUpdatingArticle: false },
      false,
      "[User] Article Edit: Update completed",
    );

    // Refetch filters to update counts
    await refetchNewsFeedFilters(feedId, dateRange, selectedFilters);

    const { selectedCount } = buildSelectionSnapshot(state);
    toast({
      type: "success",
      title: "Done! Article updated",
      description: `Successfully updated ${pluralize(selectedCount, "article")}.`,
    });

    resetEditModal();

    return {
      success: true,
      message: "Articles updated successfully",
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't update that article right now.",
    });

    useFeedContentManagementStore.setState(
      { isUpdatingArticle: false },
      false,
      "[User] Article Edit: Update failed",
    );
    return {
      success: false,
      errors: [errorMessage],
    };
  }
};

/**
 * Move articles from one narrative cluster to another
 * @param feedId - The feed ID
 * @param targetClusterId - The target narrative cluster/story ID to move articles to
 * @param dateRange - Date range for filters
 * @param selectedFilters - Selected filters to include in the mutation
 * @returns Success status and optional error messages
 */
export const moveArticleFromNarrativeCluster = async (
  feedId: string,
  targetClusterId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
) => {
  if (!feedId || !dateRange.startDate || !dateRange.endDate) {
    toast({
      type: "error",
      title: "Missing required parameters",
      description: "Feed ID, start date, or end date is missing.",
    });
    return {
      success: false,
      errors: ["Missing required parameters"],
    };
  }

  const state = useFeedContentManagementStore.getState();
  const { updateArticlesInStore } = state;
  const { selectedIds, isAllSelected, selectedCount } =
    buildSelectionSnapshot(state);

  if (selectedIds.length === 0 && !isAllSelected) {
    toast({
      type: "error",
      title: "No articles selected",
      description: "Please select at least one article to move.",
    });
    return {
      success: false,
      errors: ["No articles selected"],
    };
  }

  // Build bulk operation input structure
  const { excludedIds, feedArticleIds, projectionFilters } =
    buildBulkOperationInput(state, dateRange, selectedFilters);

  const mutationInput = {
    action: "MOVE" as const,
    excludedIds,
    feedArticleIds,
    feedId,
    projectionFilters,
    targetStoryId: targetClusterId,
  };

  // Set loading state
  useFeedContentManagementStore.setState(
    { isMovingArticleFromNarrativeCluster: true },
    false,
    "[User] Move Article: Start moving articles",
  );

  try {
    const result = await client
      .mutation(MoveOrRemoveArticlesFromNarrativeCluster, {
        input: mutationInput,
      })
      .toPromise();

    if (!result.data?.manageStoryArticlesV2) {
      throw new Error("No response data from mutation");
    }

    // The mutation returns an array of union type responses
    const responses = result.data.manageStoryArticlesV2;

    // Check for errors
    const errorInfo = handleResponseErrors(
      responses,
      "We couldn't move those articles right now.",
    );

    if (errorInfo) {
      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description:
          errorInfo.messages[0] || "We couldn't move those articles right now.",
      });

      useFeedContentManagementStore.setState(
        { isMovingArticleFromNarrativeCluster: false },
        false,
        "[User] Move Article: Move failed",
      );

      return {
        success: false,
        errors: errorInfo.messages,
      };
    }

    // Handle article list response with conditional update/refetch logic
    await handleArticleListResponse(
      responses,
      feedArticleIds,
      feedId,
      dateRange,
      selectedFilters,
      updateArticlesInStore,
    );

    // Refetch narrative cluster filters to reflect the moved articles
    await refetchNewsFeedFilters(feedId, dateRange, selectedFilters, {
      dynamicCategories: ["NARRATIVE_CLUSTER"],
    });

    const count = selectedCount;
    toast({
      type: "success",
      title: "Done! Articles moved from narrative cluster",
      description: `Moved ${count} ${pluralize(count, "article")} to the narrative cluster.`,
    });

    useFeedContentManagementStore.setState(
      { isMovingArticleFromNarrativeCluster: false },
      false,
      "[User] Move Article: Move completed",
    );

    return {
      success: true,
    };
  } catch (error) {
    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't move those articles right now.",
    });

    useFeedContentManagementStore.setState(
      { isMovingArticleFromNarrativeCluster: false },
      false,
      "[User] Move Article: Move failed",
    );

    return {
      success: false,
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};

/**
 * Remove articles from narrative cluster
 * @param feedId - The feed ID
 * @param dateRange - Date range for filters
 * @param selectedFilters - Selected filters to include in the mutation
 * @returns Success status and optional error messages
 */
export const removeArticleFromNarrativeCluster = async (
  feedId: string,
  dateRange: { startDate: string; endDate: string },
  selectedFilters: SelectedFiltersInput,
) => {
  if (!feedId || !dateRange.startDate || !dateRange.endDate) {
    toast({
      type: "error",
      title: "Missing required parameters",
      description: "Feed ID, start date, or end date is missing.",
    });
    return {
      success: false,
      errors: ["Missing required parameters"],
    };
  }

  const state = useFeedContentManagementStore.getState();
  const { updateArticlesInStore } = state;
  const { selectedIds, isAllSelected, selectedCount } =
    buildSelectionSnapshot(state);

  if (selectedIds.length === 0 && !isAllSelected) {
    toast({
      type: "error",
      title: "No articles selected",
      description: "Please select at least one article to remove.",
    });
    return {
      success: false,
      errors: ["No articles selected"],
    };
  }

  // Build bulk operation input structure
  const { excludedIds, feedArticleIds, projectionFilters } =
    buildBulkOperationInput(state, dateRange, selectedFilters);

  const mutationInput = {
    action: "REMOVE" as const,
    excludedIds,
    feedArticleIds,
    feedId,
    projectionFilters,
  };

  // Set loading state
  useFeedContentManagementStore.setState(
    { isRemovingArticleFromNarrativeCluster: true },
    false,
    "[User] Remove Article: Start removing articles",
  );

  try {
    const result = await client
      .mutation(MoveOrRemoveArticlesFromNarrativeCluster, {
        input: mutationInput,
      })
      .toPromise();

    if (!result.data?.manageStoryArticlesV2) {
      throw new Error("No response data from mutation");
    }

    // The mutation returns an array of union type responses
    const responses = result.data.manageStoryArticlesV2;

    // Check for errors
    const errorInfo = handleResponseErrors(
      responses,
      "We couldn't remove those articles right now.",
    );

    if (errorInfo) {
      toast({
        type: "error",
        title: "Oops! Something went wrong",
        description:
          errorInfo.messages[0] ||
          "We couldn't remove those articles right now.",
      });

      useFeedContentManagementStore.setState(
        { isRemovingArticleFromNarrativeCluster: false },
        false,
        "[User] Remove Article: Remove failed",
      );

      return {
        success: false,
        errors: errorInfo.messages,
      };
    }

    // Handle article list response with conditional update/refetch logic
    await handleArticleListResponse(
      responses,
      feedArticleIds,
      feedId,
      dateRange,
      selectedFilters,
      updateArticlesInStore,
    );

    // Refetch narrative cluster filters to reflect the removed articles
    await refetchNewsFeedFilters(feedId, dateRange, selectedFilters, {
      dynamicCategories: ["NARRATIVE_CLUSTER"],
    });

    const count = selectedCount;
    toast({
      type: "success",
      title: "Done! Articles removed from narrative cluster",
      description: `Removed ${count} ${pluralize(count, "article")} from the narrative cluster.`,
    });

    useFeedContentManagementStore.setState(
      { isRemovingArticleFromNarrativeCluster: false },
      false,
      "[User] Remove Article: Remove completed",
    );

    return {
      success: true,
    };
  } catch (error) {
    toast({
      type: "error",
      title: "Oops! Something went wrong",
      description: "We couldn't remove those articles right now.",
    });

    useFeedContentManagementStore.setState(
      { isRemovingArticleFromNarrativeCluster: false },
      false,
      "[User] Remove Article: Remove failed",
    );

    return {
      success: false,
      errors: [error instanceof Error ? error.message : "Unknown error"],
    };
  }
};
